import {
  NJBadge,
  NJHeading,
  NJIconButton,
  NJInlineMessage,
  NJSpinner,
} from "@engie-group/fluid-design-system-react";
import type { ColDef, ICellRendererParams } from "ag-grid-community";
import { useMemo } from "react";
import { DataTable } from "@/components/DataTable";
import type { AccessRequestRead } from "@/types";
import { useAccessRequests, useReviewAccessRequest } from "./useAccessRequests";

const STATUS_VARIANT: Record<string, "warning" | "success" | "danger"> = {
  PENDING: "warning",
  APPROVED: "success",
  REFUSED: "danger",
};

function StatusCell({ data }: ICellRendererParams<AccessRequestRead>) {
  if (!data) return null;
  return (
    <NJBadge variant={STATUS_VARIANT[data.status] ?? "neutral"}>
      {data.status}
    </NJBadge>
  );
}

function ActionsCell({ data }: ICellRendererParams<AccessRequestRead>) {
  const reviewAccessRequest = useReviewAccessRequest();

  if (!data || data.status !== "PENDING") {
    return null;
  }

  return (
    <div className="flex items-center gap-1">
      <NJIconButton
        icon="check"
        variant="tertiary"
        label="Approve request"
        isDisabled={reviewAccessRequest.isPending}
        onClick={() =>
          reviewAccessRequest.mutate({ id: data.id, status: "APPROVED" })
        }
      />
      <NJIconButton
        icon="close"
        variant="destructive"
        label="Reject request"
        isDisabled={reviewAccessRequest.isPending}
        onClick={() =>
          reviewAccessRequest.mutate({ id: data.id, status: "REFUSED" })
        }
      />
    </div>
  );
}

/** Validator inbox — table of access requests within the
 * caller's validator scope, with inline approve/reject actions for `PENDING` rows. */
export function ValidatorInboxPage() {
  const { data: requests, isLoading, isError } = useAccessRequests();

  const columnDefs = useMemo<ColDef<AccessRequestRead>[]>(
    () => [
      {
        headerName: "User",
        valueGetter: (params) =>
          params.data
            ? `${params.data.requester.firstName} ${params.data.requester.lastName}`
            : "",
        flex: 1,
      },
      { headerName: "Agent", field: "agentName", flex: 1 },
      {
        headerName: "Requested at",
        field: "requestedAt",
        flex: 1,
        valueFormatter: (params) =>
          params.value ? new Date(params.value as string).toLocaleString() : "",
      },
      {
        headerName: "Status",
        cellRenderer: StatusCell,
        flex: 1,
        sortable: false,
      },
      {
        headerName: "Actions",
        cellRenderer: ActionsCell,
        flex: 1,
        sortable: false,
      },
    ],
    [],
  );

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center py-12">
        <NJSpinner aria-label="Loading access requests" />
      </div>
    );
  }

  if (isError) {
    return (
      <NJInlineMessage variant="error">
        Could not load the access requests.
      </NJInlineMessage>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <NJHeading as="h1" scale="lg">
        Access requests to review
      </NJHeading>
      <DataTable<AccessRequestRead>
        rowData={requests ?? []}
        columnDefs={columnDefs}
      />
    </div>
  );
}
